/**
 * Sperrgründe zurückgehaltener Aufträge: Klartext (so sieht ihn der Kunde) mit stabilen Präfixen.
 * Die Einordnung je Art ist Grundlage für „Handlungsbedarf“, Filter in der Auftragsliste und Benachrichtigungen.
 */

/** Sperrgrund, den der Kunde sieht, wenn Logiful die Adresse klärt (Details nur intern) */
export const ADDRESS_HOLD_LOGIFUL = "In Prüfung bei Logiful (Adresse)";
export const ADDRESS_HOLD_PREFIX = "Adresse prüfen: ";
/** Markierung am Auftrag: bei Logiful storniert und in Shopify an den Händler zurückgegeben */
export const RETURNED_TO_SHOP_TAG = "an-shop-zurueckgegeben";
/** Haltegrund für Aufträge im Testbetrieb */
export const TEST_MODE_HOLD = "Testbetrieb – nicht ans Lager übertragen";
/** Umzug von FFN Connect läuft: Übergabe kurz angehalten, damit freiwerdender Bestand nach Bestelldatum vergeben wird */
export const MIGRATION_HOLD = "Umstellung von FFN Connect läuft – geht danach in Bestelldatum-Reihenfolge weiter";
/** aus dem Lager zurückgeholt: gewollt, Bestand bleibt reserviert, bis „Freigeben“ */
export const RECALLED_HOLD = "Aus dem Lager zurückgeholt – Bestand bleibt reserviert, „Freigeben“ überträgt ihn wieder";
/** Freigabetermin des Kunden: „Freigabetermin: ab 27.10.2026“ – gewollt, daher kein Handlungsbedarf */
export const DEFERRED_HOLD_PREFIX = "Freigabetermin: ";
/** Komponenten eines Produktionsauftrags: reserviert, bis Logiful die Produktion startet (kein Handlungsbedarf) */
export const PRODUCTION_HOLD = "Produktion: wartet auf Start durch Logiful";

export type HoldKind =
  | "address" | "address_logiful" | "address_incomplete" | "payment" | "unknown_sku" | "master_data" | "insert_stock" | "backorder" | "batch" | "b2b_requirement" | "fba_labels" | "export_document" | "test_mode" | "production" | "deferred" | "billing" | "migration" | "recalled" | "rule";
export const HOLD_KINDS: HoldKind[] = ["address", "address_logiful", "address_incomplete", "payment", "unknown_sku", "master_data", "insert_stock", "backorder", "batch", "b2b_requirement", "fba_labels", "export_document", "test_mode", "production", "deferred", "billing", "migration", "recalled", "rule"];

/** feste Sperrgründe/Präfixe je Art (alles andere = eigene Regel des Kunden) */
export const HOLD_PATTERNS: { kind: Exclude<HoldKind, "rule">; equals?: string; prefix?: string }[] = [
  { kind: "address", prefix: ADDRESS_HOLD_PREFIX },
  { kind: "address_logiful", equals: ADDRESS_HOLD_LOGIFUL },
  { kind: "address_incomplete", equals: "Lieferadresse unvollständig" },
  { kind: "payment", equals: "Zahlung nicht abgeschlossen" },
  { kind: "unknown_sku", prefix: "Unbekannte Artikel" },
  { kind: "master_data", prefix: "Stammdaten unvollständig" },
  { kind: "insert_stock", prefix: "Beilage out of stock" },
  { kind: "backorder", prefix: "Fehlbestand" },
  { kind: "batch", prefix: "Vorgegebene Charge" },
  { kind: "b2b_requirement", prefix: "Händler-Vorgabe" },
  { kind: "fba_labels", prefix: "Amazon-Etiketten fehlen" },
  { kind: "export_document", prefix: "Ausfuhrbegleitdokument" },
  { kind: "test_mode", equals: TEST_MODE_HOLD },
  { kind: "test_mode", prefix: "Würde abgelehnt" },
  { kind: "production", equals: PRODUCTION_HOLD },
  { kind: "deferred", prefix: DEFERRED_HOLD_PREFIX },
  { kind: "billing", prefix: "Offene Rechnungen: " },
  { kind: "migration", equals: MIGRATION_HOLD },
  { kind: "recalled", equals: RECALLED_HOLD },
];

/** Sperrgrund → Art */
export function holdKind(reason: string): HoldKind {
  for (const p of HOLD_PATTERNS) if (p.equals ? reason === p.equals : reason.startsWith(p.prefix!)) return p.kind;
  return "rule";
}

export const HOLD_KIND_LABEL: Record<HoldKind, string> = {
  address: "Adresse prüfen",
  address_logiful: "Adresse in Klärung bei Logiful",
  address_incomplete: "Adresse unvollständig",
  payment: "wartet auf Zahlung",
  unknown_sku: "unbekannte Artikel",
  master_data: "Stammdaten fehlen",
  insert_stock: "Beilage nicht auf Lager",
  backorder: "wartet auf Ware",
  batch: "vorgegebene Charge fehlt",
  b2b_requirement: "Händler-Vorgabe nicht erfüllbar",
  fba_labels: "wartet auf Amazon-Etiketten",
  export_document: "wartet auf Ausfuhrbegleitdokument",
  test_mode: "Testbetrieb",
  production: "wartet auf Produktionsstart",
  deferred: "Freigabetermin",
  billing: "pausiert (offene Rechnungen)",
  migration: "Umstellung läuft",
  recalled: "aus dem Lager zurückgeholt",
  rule: "von Regel zurückgehalten",
};

/** Freigabetermine: frühestens morgen, höchstens 90 Tage im Voraus */
export const DEFER_MAX_DAYS = 90;

/** Freigabe am Termin um 6:00 Uhr (Europe/Berlin) – Datum „YYYY-MM-DD“ → Zeitpunkt; ungültig → null */
export function deferralReleaseAt(date: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  for (const offset of [1, 2]) {
    const d = new Date(`${date}T${String(6 - offset).padStart(2, "0")}:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return null;
    const local = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23" }).format(d);
    if (local === `${date} 06`) return d;
  }
  return null;
}

/** Ist das Datum erlaubt? (morgen bis +90 Tage, Berliner Kalendertag) */
export function deferralAllowed(date: string, now = new Date()): boolean {
  const at = deferralReleaseAt(date);
  if (!at) return false;
  const today = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(now);
  return date > today && at.getTime() - now.getTime() <= (DEFER_MAX_DAYS + 1) * 86_400_000;
}

/** Sperrgrund für den Kunden: „Freigabetermin: geht am 27.10.2026 ans Lager“ */
// Text ausgeschrieben (nicht über DEFERRED_HOLD_PREFIX), damit die Übersetzung der Servertexte ihn findet
const berlinDay = (d: Date) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
export const deferredHoldReason = (until: Date) => `Freigabetermin: geht am ${berlinDay(until)} ans Lager`;
